import { Injectable } from '@angular/core';

import {
  Dashboard,
  ThemeColors,
  DashboardPage,
  DashboardPresentationSettings,
  getDefaultPresentationSettings,
} from './models/core.models';

// ! Inheritable page property - only duration remains as page-level customizable property
// ! This property can be customized per page and inherits from global settings when null
export const INHERITABLE_PAGE_PROPERTIES = {
  duration: ['duration_seconds'] as const,
} as const;

@Injectable({
  providedIn: 'root',
})
export class PresentationNormalizerService {
  // ! Normalize global presentation settings with defaults
  // ! Ensures all properties have values (no nulls/undefined)
  normalizeGlobalSettings(
    settings: Partial<DashboardPresentationSettings> | undefined,
  ): DashboardPresentationSettings {
    const defaults = getDefaultPresentationSettings();

    const normalized: DashboardPresentationSettings = {
      ...defaults,
      ...settings,
    };

    // ! handle nested ThemeColors objects explicitly
    if (settings?.dashboard_title_color) {
      normalized.dashboard_title_color = this.mergeThemeColors(
        settings.dashboard_title_color,
        defaults.dashboard_title_color,
      );
    }

    if (settings?.page_title_color) {
      normalized.page_title_color = this.mergeThemeColors(
        settings.page_title_color,
        defaults.page_title_color,
      );
    }

    if (settings?.time_period_color) {
      normalized.time_period_color = this.mergeThemeColors(
        settings.time_period_color,
        defaults.time_period_color,
      );
    }

    if (settings?.timer_color) {
      normalized.timer_color = this.mergeThemeColors(
        settings.timer_color,
        defaults.timer_color,
      );
    }

    if (settings?.pagination_color) {
      normalized.pagination_color = this.mergeThemeColors(
        settings.pagination_color,
        defaults.pagination_color,
      );
    }

    return normalized;
  }

  // ! Normalize a single page by inheriting null properties from global settings
  // ! Now simplified - only duration_seconds can be customized per page
  normalizePage(
    page: DashboardPage,
    globalSettings: DashboardPresentationSettings,
  ): DashboardPage {
    const result = page.duration_seconds || globalSettings.duration_seconds;

    return {
      ...page,
      // * Apply global duration if page has null value
      duration_seconds: result,
    };
  }

  normalizeAllPages(
    pages: DashboardPage[],
    globalSettings: DashboardPresentationSettings,
  ): DashboardPage[] {
    return pages.map((page) => this.normalizePage(page, globalSettings));
  }

  // ! Normalize entire dashboard (global settings + all pages)
  // ! This is the main entry point for reducer usage
  normalizeDashboard(dashboard: Dashboard): Dashboard {
    // * Step 1: Normalize global settings with defaults
    const normalizedSettings = this.normalizeGlobalSettings(
      dashboard.presentation_settings,
    );

    // * Step 2: Normalize pages using complete global settings
    const normalizedPages = dashboard.pages
      ? this.normalizeAllPages(dashboard.pages, normalizedSettings)
      : [];

    return {
      ...dashboard,
      presentation_settings: normalizedSettings,
      pages: normalizedPages,
    };
  }

  private mergeThemeColors(
    pageColors: Partial<ThemeColors> | ThemeColors | null | undefined,
    globalColors: ThemeColors,
  ): ThemeColors {
    return {
      light: pageColors?.light ?? globalColors.light,
      dark: pageColors?.dark ?? globalColors.dark,
    };
  }
}
